import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { hu } from 'date-fns/locale';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack,
  Person,
  ZoomIn,
  Phone,
  TouchApp,
  Share,
  PhotoCamera,
  EventNote,
  Timer,
  PhotoLibrary,
  HomeOutlined,
  TrendingUp,
  TrendingDown,
  Facebook,
  Instagram,
  Language,
  HelpOutline,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  Legend,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import { fetchProviderStats, fetchProviderStatsSummary } from '../../services/analyticsService';
import DateRangeSelector from '../../components/Stats/DateRangeSelector';
import FunnelDisplay from '../../components/Stats/FunnelDisplay';

const KPI_CONFIG = [
  { key: 'HOME_IMPRESSION',    label: 'Főoldali megjelenés',      icon: HomeOutlined, color: '#b8860b', hint: 'Hányszor jelent meg a kártyád a főoldalon (Kiemelt partnereink, kategória-blokkok, Új a platformon). Egy látogatásnál sávonként egyszer számít, ha a kártya legalább félig látszott.' },
  { key: 'PROFILE_VIEW',      label: 'Profil megtekintés',       icon: Person,       color: '#2a5d32', hint: 'Hányszor nyitották meg a teljes profiloldalad a látogatók.' },
  { key: 'QUICK_VIEW_OPEN',    label: 'Gyors megtekintés',        icon: ZoomIn,       color: '#5b8159', hint: 'Hányszor nyitotta meg valaki az előnézet ablakot a kereső oldalon.' },
  { key: 'CONTACT_REVEAL',     label: 'Kapcsolat felfedés',       icon: Phone,        color: '#4caf50', hint: 'Hányszor kattintottak a „Kapcsolati adatok mutatása" gombra a profilodon.' },
  { key: 'CONTACT_LINK_CLICK', label: 'Kapcsolat kattintás',      icon: TouchApp,     color: '#ff9800', hint: 'Telefonszámra, email címre vagy helyszín linkre kattintások száma.' },
  { key: 'SOCIAL_CLICK',       label: 'Közösségi média',          icon: Share,        color: '#1877f2', hint: 'Hányszor kattintottak a Facebook, Instagram, TikTok vagy weboldal ikonra.' },
  { key: 'GALLERY_SWIPE',      label: 'Galéria lapozás',          icon: PhotoCamera,  color: '#9c27b0', hint: 'Hányszor lapoztak a képek között (galéria, lightbox és gyors nézet összesen).' },
  { key: 'OFFER_REQUEST',      label: 'Ajánlatkérés',             icon: EventNote,    color: '#f44336', hint: 'Sikeres ajánlatkérési űrlap beküldések száma.' },
  { key: '_avg_time',          label: 'Átl. profil idő',          icon: Timer,        color: '#795548', hint: 'Átlagosan mennyi ideig nézegetik a profilodat. Csak a legalább 5 másodperces látogatásokból számítva.' },
  { key: '_gallery_rate',      label: 'Galéria elkötelezettség',  icon: PhotoLibrary, color: '#009688', hint: 'A látogatók hány százaléka lapozta a galériát is. (Galéria lapozások / Profil megtekintések × 100)' },
];

const CHART_COLORS = {
  HOME_IMPRESSION:    '#b8860b',
  PROFILE_VIEW:       '#2a5d32',
  QUICK_VIEW_OPEN:    '#5b8159',
  CONTACT_REVEAL:     '#4caf50',
  CONTACT_LINK_CLICK: '#ff9800',
  SOCIAL_CLICK:       '#1877f2',
  GALLERY_SWIPE:      '#9c27b0',
  OFFER_REQUEST:      '#f44336',
};

const EVENT_LABELS = {
  HOME_IMPRESSION:    'Főoldali megjelenés',
  PROFILE_VIEW:       'Profil megtekintés',
  QUICK_VIEW_OPEN:    'Gyors megtekintés',
  CONTACT_REVEAL:     'Kapcsolat felfedés',
  CONTACT_LINK_CLICK: 'Kapcsolat kattintás',
  SOCIAL_CLICK:       'Közösségi média',
  GALLERY_SWIPE:      'Galéria lapozás',
  OFFER_REQUEST:      'Ajánlatkérés',
};

const SOCIAL_LABELS = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  website: 'Weboldal',
};

const SOCIAL_COLORS = {
  facebook: '#1877f2',
  instagram: '#e1306c',
  tiktok: '#000000',
  website: '#2a5d32',
};

const CONTACT_LABELS = {
  phone: 'Telefon',
  email: 'Email',
  address: 'Cím',
};

const formatSeconds = (s) => {
  if (!s || s === 0) return '–';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (m === 0) return `${sec} mp`;
  return `${m} perc ${sec > 0 ? sec + ' mp' : ''}`.trim();
};

// ─── Főoldal ─────────────────────────────────────────────────────────────────
const ProviderStatsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedDays, setSelectedDays] = useState(30);
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [isCustom, setIsCustom] = useState(false);

  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [visibleSeries, setVisibleSeries] = useState(
    new Set(['PROFILE_VIEW', 'QUICK_VIEW_OPEN', 'OFFER_REQUEST'])
  );

  const getDateRange = useCallback(() => {
    if (isCustom && customStart && customEnd) {
      return {
        startDate: format(customStart, 'yyyy-MM-dd'),
        endDate: format(customEnd, 'yyyy-MM-dd'),
      };
    }
    const end = new Date();
    const start = subDays(end, selectedDays - 1);
    return {
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(end, 'yyyy-MM-dd'),
    };
  }, [selectedDays, isCustom, customStart, customEnd]);

  useEffect(() => {
    if (isCustom && (!customStart || !customEnd)) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { startDate, endDate } = getDateRange();
        const [statsRes, summaryRes] = await Promise.all([
          fetchProviderStats(startDate, endDate),
          fetchProviderStatsSummary(startDate, endDate),
        ]);
        if (statsRes.success) setStats(statsRes.data);
        if (summaryRes.success) setSummary(summaryRes.data);
      } catch (e) {
        setError('Nem sikerült betölteni a statisztikákat.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [selectedDays, isCustom, customStart, customEnd, getDateRange]);

  // Grafikon adatok transzformálása
  const chartData = useMemo(() => {
    if (!stats?.events) return [];
    const byDate = {};
    stats.events.forEach(({ date, eventType, count }) => {
      const d = typeof date === 'string' ? date : format(new Date(date), 'yyyy-MM-dd');
      if (!byDate[d]) byDate[d] = { date: d };
      byDate[d][eventType] = count;
    });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  }, [stats]);

  const formatXAxis = (dateStr) => {
    try {
      return format(new Date(dateStr + 'T12:00:00'), selectedDays <= 7 ? 'MM.dd.' : 'MM.dd.', { locale: hu });
    } catch {
      return dateStr;
    }
  };

  const toggleSeries = (key) => {
    setVisibleSeries((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size > 1) next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const socialData = useMemo(() => {
    if (!summary?.socialBreakdown) return [];
    return Object.entries(summary.socialBreakdown)
      .filter(([, v]) => v > 0)
      .map(([k, v]) => ({ name: SOCIAL_LABELS[k] || k, value: v, color: SOCIAL_COLORS[k] || '#999' }));
  }, [summary]);

  const contactData = useMemo(() => {
    if (!summary?.contactBreakdown) return [];
    return Object.entries(summary.contactBreakdown)
      .filter(([, v]) => v > 0)
      .map(([k, v]) => ({ name: CONTACT_LABELS[k] || k, value: v }));
  }, [summary]);

  const hasData = chartData.length > 0 || (summary && Object.values(summary.totals || {}).some((v) => v > 0));

  // A főoldali megjelenésnél a súgóban a sávonkénti bontás is látszik
  const kpiHint = (kpi) => {
    const byBand = summary?.homeImpressionsByBand;
    if (kpi.key !== 'HOME_IMPRESSION' || !byBand) return kpi.hint;
    return `${kpi.hint} Bontás: Kiemelt partnereink ${byBand.featured}, kategória-blokk ${byBand.category}, Új a platformon ${byBand.new}.`;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={hu}>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pb: 6 }}>
        {/* Fejléc */}
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            px: { xs: 2, md: 4 },
            py: 2,
          }}
        >
          <Container maxWidth="xl" disableGutters>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <IconButton onClick={() => navigate('/dashboard')} size="small">
                <ArrowBack />
              </IconButton>
              <Box>
                <Typography variant="h5" fontWeight={700} fontFamily="'Playfair Display', serif">
                  Statisztikák
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Profilod teljesítménye és elkötelezettsége
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="xl" sx={{ pt: 3 }}>
          {/* Időszak választó */}
          <DateRangeSelector
            selectedDays={selectedDays}
            setSelectedDays={setSelectedDays}
            isCustom={isCustom}
            setIsCustom={setIsCustom}
            customStart={customStart}
            setCustomStart={setCustomStart}
            customEnd={customEnd}
            setCustomEnd={setCustomEnd}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>
          )}

          {/* KPI csík */}
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, mb: 3, overflow: 'hidden' }}>
            <Box sx={{
              display: 'flex',
              overflowX: 'auto',
              '&::-webkit-scrollbar': { height: 3 },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 },
            }}>
              {KPI_CONFIG.map((kpi, idx) => {
                const isComputed = kpi.key.startsWith('_');
                let value, prevValue, trend;
                if (kpi.key === '_avg_time') {
                  value = summary?.avgTimeSeconds;
                } else if (kpi.key === '_gallery_rate') {
                  value = summary?.galleryEngagementRate;
                } else {
                  value = summary?.totals?.[kpi.key];
                  prevValue = summary?.previousPeriodTotals?.[kpi.key];
                  if (!isComputed && prevValue !== undefined && prevValue !== null) {
                    if (prevValue === 0 && value > 0) trend = { pct: 100, dir: 'up' };
                    else if (prevValue > 0) {
                      const pct = Math.round(((value - prevValue) / prevValue) * 100);
                      trend = { pct: Math.abs(pct), dir: pct >= 0 ? 'up' : 'down' };
                    }
                  }
                }

                const KpiIcon = kpi.icon;
                const displayVal = () => {
                  if (loading) return '–';
                  if (kpi.key === '_avg_time') return formatSeconds(value);
                  if (kpi.key === '_gallery_rate') return value !== undefined ? `${value}%` : '–';
                  return (value ?? 0).toLocaleString('hu-HU');
                };

                return (
                  <Tooltip key={kpi.key} title={kpiHint(kpi)} placement="bottom" arrow enterTouchDelay={50}>
                    <Box
                      sx={{
                        flex: '1 1 0',
                        minWidth: 105,
                        px: { xs: 1.5, md: 2 },
                        py: 1.5,
                        borderRight: idx < KPI_CONFIG.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider',
                        '&:hover': { bgcolor: 'action.hover' },
                        cursor: 'default',
                        userSelect: 'none',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                        <Box sx={{
                          width: 24, height: 24, borderRadius: 1, flexShrink: 0,
                          bgcolor: kpi.color + '1a',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <KpiIcon sx={{ color: kpi.color, fontSize: 14 }} />
                        </Box>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, lineHeight: 1.2, fontSize: { xs: '0.82rem', md: '0.875rem' } }}>
                          {loading ? <CircularProgress size={12} /> : displayVal()}
                        </Typography>
                        {trend && !loading && (
                          <Typography variant="caption" fontWeight={700} sx={{
                            color: trend.dir === 'up' ? 'success.main' : 'error.main',
                            whiteSpace: 'nowrap', fontSize: '0.65rem',
                          }}>
                            {trend.dir === 'up' ? '↑' : '↓'}{trend.pct}%
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3, fontSize: '0.68rem' }}>
                          {kpi.label}
                        </Typography>
                        <HelpOutline sx={{ fontSize: 11, color: 'text.disabled', flexShrink: 0 }} />
                      </Box>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Card>

          {/* Fő area chart */}
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, mb: 3 }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
                  Aktivitás időbeli alakulása
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.75,
                    overflowX: 'auto',
                    pb: 0.5,
                    '&::-webkit-scrollbar': { height: 4 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 },
                  }}
                >
                  {Object.entries(EVENT_LABELS).map(([key, label]) => (
                    <Chip
                      key={key}
                      label={label}
                      size="small"
                      onClick={() => toggleSeries(key)}
                      sx={{
                        flexShrink: 0,
                        borderRadius: '6px',
                        fontSize: '0.72rem',
                        fontWeight: visibleSeries.has(key) ? 600 : 400,
                        bgcolor: visibleSeries.has(key) ? CHART_COLORS[key] + '20' : 'transparent',
                        color: visibleSeries.has(key) ? CHART_COLORS[key] : 'text.disabled',
                        border: `1.5px solid ${visibleSeries.has(key) ? CHART_COLORS[key] : '#e0e0e0'}`,
                        cursor: 'pointer',
                        '&:hover': { opacity: 0.8 },
                      }}
                    />
                  ))}
                </Box>
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : !hasData ? (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  Ebben az időszakban nem volt aktivitás. Próbálj meg szélesebb időszakot választani!
                </Alert>
              ) : (
                <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
                  <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      {Object.entries(CHART_COLORS).map(([key, color]) => (
                        <linearGradient key={key} id={`grad_${key}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={color} stopOpacity={0.2} />
                          <stop offset="95%" stopColor={color} stopOpacity={0} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatXAxis}
                      tick={{ fontSize: 11, fill: '#888' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#888' }}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <ReTooltip
                      contentStyle={{ borderRadius: 8, border: '1px solid #eee', fontSize: 12 }}
                      formatter={(value, name) => [value, EVENT_LABELS[name] || name]}
                      labelFormatter={(label) => {
                        try {
                          return format(new Date(label + 'T12:00:00'), 'yyyy. MM. dd.', { locale: hu });
                        } catch {
                          return label;
                        }
                      }}
                    />
                    {Object.entries(CHART_COLORS).map(([key, color]) =>
                      visibleSeries.has(key) ? (
                        <Area
                          key={key}
                          type="monotone"
                          dataKey={key}
                          name={key}
                          stroke={color}
                          strokeWidth={2}
                          fill={`url(#grad_${key})`}
                          dot={false}
                          activeDot={{ r: 4, strokeWidth: 0 }}
                        />
                      ) : null
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* 3 alsó szekció – egyforma magasság, mobilon lapozható */}
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 1.5, md: 3 },
              overflowX: 'auto',
              scrollSnapType: 'x mandatory',
              pb: 0.5,
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
          >
            {/* Social média bontás */}
            <Card
              elevation={0}
              sx={{
                border: '1px solid', borderColor: 'divider', borderRadius: 3,
                flex: { xs: '0 0 calc(100% - 20px)', md: '1 1 0' },
                minWidth: { xs: 260, md: 0 },
                scrollSnapAlign: 'start',
                display: 'flex', flexDirection: 'column',
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                  Közösségi média kattintások
                </Typography>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 4 }}><CircularProgress /></Box>
                  ) : socialData.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center', width: '100%' }}>
                      Még nincs adat
                    </Typography>
                  ) : (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={socialData} margin={{ top: 0, right: 0, left: -30, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                        <ReTooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                        <Bar dataKey="value" name="Kattintás" radius={[4, 4, 0, 0]}>
                          {socialData.map((entry) => (
                            <Cell key={entry.name} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Kapcsolat interakciók */}
            <Card
              elevation={0}
              sx={{
                border: '1px solid', borderColor: 'divider', borderRadius: 3,
                flex: { xs: '0 0 calc(100% - 20px)', md: '1 1 0' },
                minWidth: { xs: 260, md: 0 },
                scrollSnapAlign: 'start',
                display: 'flex', flexDirection: 'column',
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                  Kapcsolat interakciók
                </Typography>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 4 }}><CircularProgress /></Box>
                  ) : contactData.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center', width: '100%' }}>
                      Még nincs adat
                    </Typography>
                  ) : (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={contactData} margin={{ top: 0, right: 0, left: -30, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                        <ReTooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                        <Bar dataKey="value" name="Kattintás" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Konverziós tölcsér */}
            <Card
              elevation={0}
              sx={{
                border: '1px solid', borderColor: 'divider', borderRadius: 3,
                flex: { xs: '0 0 calc(100% - 20px)', md: '1 1 0' },
                minWidth: { xs: 260, md: 0 },
                scrollSnapAlign: 'start',
                display: 'flex', flexDirection: 'column',
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                  Konverziós tölcsér
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <FunnelDisplay funnel={summary?.funnel} loading={loading} />
                  {!loading && summary && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary">
                        Összesített profil megtekintések (minden idők)
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="primary.main">
                        {(summary.cumulativeProfileViews || 0).toLocaleString('hu-HU')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Container>
      </Box>
    </LocalizationProvider>
  );
};

export default ProviderStatsPage;
